import React from "react";
import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";

export type OperationalKpiCardProps = {
  title: string;
  /** Primary metric — number or formatted string */
  value: string | number;
  secondaryText?: string;
  icon?: React.ReactNode;
  /** Optional trend hint (TODO: wire when API provides series) */
  trendLabel?: string;
  loading?: boolean;
  empty?: boolean;
  emptyMessage?: string;
  onClick?: () => void;
};

export const OperationalKpiCard = ({
  title,
  value,
  secondaryText,
  icon,
  trendLabel,
  loading,
  empty,
  emptyMessage = "No data",
  onClick,
}: OperationalKpiCardProps) => {
  const clickable = typeof onClick === "function";

  return (
    <Paper
      elevation={0}
      onClick={clickable ? onClick : undefined}
      sx={{
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        height: "100%",
        cursor: clickable ? "pointer" : "default",
        transition: "box-shadow 0.2s ease",
        "&:hover": clickable
          ? {
              boxShadow: 1,
            }
          : {},
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {title}
          </Typography>
          {icon ? (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span className="material-icons-outlined" style={{ fontSize: 20, color: "inherit" }}>
                {icon}
              </span>
            </Box>
          ) : null}
        </Stack>

        {loading ? (
          <OperationalKpiCardSkeleton />
        ) : empty ? (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        ) : (
          <>
            <Typography variant="h4" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
              {value}
            </Typography>
            {secondaryText ? (
              <Typography variant="caption" color="text.secondary">
                {secondaryText}
              </Typography>
            ) : null}
            {trendLabel ? (
              <Typography variant="caption" color="text.disabled">
                {trendLabel}
              </Typography>
            ) : null}
          </>
        )}
      </Stack>
    </Paper>
  );
};

export const OperationalKpiCardSkeleton = () => (
  <Stack spacing={0.5}>
    <Skeleton variant="rounded" height={36} />
    <Skeleton variant="text" width="60%" height={20} />
  </Stack>
);
